import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useMemo, useState } from "react";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import OpsImageService from "../services/OpsImageService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Site from "../models/Site";
import ProjectService from "../services/ProjectService";
import { QueryKeyFactory } from "../services/queryKeys";
import SiteService from "../services/SiteService";
import { randomId } from "../utils/FileUtils";
import { unwrap } from "../utils/queryFn";
import RoleUtils from "../utils/RoleUtils";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";

export const siteKeys = new QueryKeyFactory(SiteService.path);
export const projectNameKeys = new QueryKeyFactory(ProjectService.path);

export type SiteListFilters = {
  projectExternalId?: string;
  role?: string;
  title?: string;
  isArchived?: boolean;
};

const RECOMMENDED_PAGE = 0;
const RECOMMENDED_PAGE_SIZE = 10;
const NO_PROJECTS: AutoCompleteUIDTO[] = [];

function recommendedSitesKey(page: number, pageSize: number) {
  return siteKeys.list({ page, pageSize, recommended: true });
}

export const useSiteList = (filters: SiteListFilters = {}) => {
  const queryClient = useQueryClient();
  const projectExternalId = filters.projectExternalId || "All";
  const role = filters.role || "All";
  const title = filters.title ?? "";
  const isArchived = filters.isArchived ?? false;

  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [isUploading, setIsUploading] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [searchRoleKey, setSearchRoleKey] = useState<AutoCompleteUIDTO>({ externalId: "All", label: "All" });
  const [selectedSite, setSelectedSite] = useState<Site | undefined>(undefined);
  const [viewSite, setViewSite] = useState<Site | undefined>();
  const [editSite, setEditSite] = useState<Site | undefined>();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | undefined>(undefined);

  const isAdmin = RoleUtils.isAdmin();
  const isManager = RoleUtils.isManager();

  const adminProjectsQuery = useQuery({
    queryKey: projectNameKeys.list({ scope: "admin" }),
    queryFn: () => unwrap(ProjectService.findProjectNames()),
    enabled: isAdmin,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const managerProjectsQuery = useQuery({
    queryKey: projectNameKeys.list({ scope: "manager" }),
    queryFn: () => unwrap(ProjectService.findProjectsNamesByUserManagerExternalId()),
    enabled: !isAdmin && isManager,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const projects = adminProjectsQuery.data ?? NO_PROJECTS;
  const projectsManager = managerProjectsQuery.data ?? NO_PROJECTS;

  const recommendedSitesQuery = useQuery({
    queryKey: recommendedSitesKey(RECOMMENDED_PAGE, RECOMMENDED_PAGE_SIZE),
    queryFn: () => unwrap(SiteService.getByProjectAndRole(RECOMMENDED_PAGE, RECOMMENDED_PAGE_SIZE)),
    enabled: false,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Site>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          siteKeys.list({ page, pageSize, sortBy, sortDir, projectExternalId, role, title, isArchived }),
        queryFn: (page, pageSize) => unwrap(SiteService.getSiteFilter(page, pageSize, projectExternalId, role, title, isArchived)),
        client: queryClient,
      }),
    [queryClient, projectExternalId, role, title, isArchived],
  );

  const getRecommendedSites = useMemo(
    () =>
      createTableQuery<Site>({
        queryKey: (page, pageSize) => recommendedSitesKey(page, pageSize),
        queryFn: (page, pageSize) => unwrap(SiteService.getByProjectAndRole(page, pageSize)),
        client: queryClient,
      }),
    [queryClient],
  );

  const refreshTable = () => refreshTableQuery(siteKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const createMutation = useMutation({
    mutationFn: (site: Site) => unwrap(SiteService.create(site)),
    onSuccess: async () => {
      setSelectedFile("");
      await refreshTable();
      NotificationService.info("Site created successfully");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (site: Site) => unwrap(SiteService.editSite(site)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Site updated successfully");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (site: Site) => unwrap(SiteService.delete(site)),
    onSuccess: async () => {
      setSelectedSite(undefined);
      await refreshTable();
      NotificationService.info("Site deleted successfully");
    },
  });

  const handleOnDeleteSite = () => {
    if (!selectedSite) return;
    deleteMutation.mutate(selectedSite);
  };

  const handleOnCreateSite = (site: Site) => {
    createMutation.mutate(site);
  };

  const handleOnUpdate = (site: Site) => {
    updateMutation.mutate(site);
  };

  const handleOpenViewDialog = (site: Site) => {
    setViewSite(site);
  };

  const handleOpenEditDialog = (site: Site) => {
    setEditSite(site);
  };

  const getProjectNames = () => {
    if (isAdmin) {
      void adminProjectsQuery.refetch();
    } else if (isManager) {
      void managerProjectsQuery.refetch();
    }
  };

  const closeCreateDialog = () => {
    setOpenCreateModal(false);
    setSelectedFile(undefined);
  };

  const closeEditDialog = () => {
    setEditSite(undefined);
    setSelectedFile(undefined);
  };

  const handleImageDelete = () => {
    setSelectedFile("");
  };

  const handleUploadImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    setIsUploading(true);
    const fileToUpload = fileList[0];
    const filename = `ensolvers-ops-site-image_${randomId()}`;
    try {
      const image = await unwrap(OpsImageService.uploadImage(fileToUpload, filename));
      setSelectedFile(image.url);
    } catch {
      NotificationService.error("This file can not be uploaded. Please try with a different one");
    } finally {
      setIsUploading(false);
    }
  };

  const isLoading = isUploading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const result = recommendedSitesQuery.data?.totalElements;

  return {
    onDataFetch,
    forceUpdateOn,
    isLoading,
    openCreateModal,
    selectedFile,
    siteActions: {
      search,
      setSearch,
      isLoading,
      setIsLoading: setIsUploading,
      editMode,
      setEditMode,
      searchRoleKey,
      setSearchRoleKey,
      selectedSite,
      setSelectedSite,
      handleOnDeleteSite,
      handleOnCreateSite,
      handleOnUpdate,
      getProjectNames,
      projects,
      projectsManager,
      handleOpenViewDialog,
      handleOpenEditDialog,
      viewSite,
      setViewSite,
      setEditSite,
      editSite,
      projectsAdminManager: [...projects, ...projectsManager],
      result,
      getRecommendedSites,
      handleUploadImage,
      handleImageDelete,
      closeCreateDialog,
      closeEditDialog,
      setOpenCreateModal,
    },
  };
};
